// ---- helpers ----
export const fmtNum = (n, opts = {}) => {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 3, ...opts }).format(Number(n));
  // eslint-disable-next-line
};

export const fmtInt = (n) => (isFinite(n) ? new Intl.NumberFormat().format(Math.round(n)) : "—");

export const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
};

export const humanInterval = (secStr) => {
  const sec = Number(secStr);
  if (!isFinite(sec) || sec <= 0) return "—";
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const mins = Math.floor((sec % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};


/**
 * Truncate a string to maxLen (including ellipsis), safely handling emoji.
 * Options:
 *  - position: 'end' | 'start' | 'middle'  (default: 'end')
 *  - ellipsis: string, e.g. '…' or '...'   (default: '…')
 *  - keepWholeWords: boolean               (default: false; only for position='end')
 */
// ---- portfolio / workspace helpers (extracted from PortfolioInfo.jsx) ----

export const formatCurrency = (value) => {
  const num = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatPercent = (value) => {
  const num = parseFloat(value);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
};

export const formatPercentUnsigned = (value) => {
  const num = parseFloat(value);
  return `${num >= 0 ? '' : ''}${num.toFixed(2)}%`;
};

export const parseUtcDate = (isoString) => {
  if (!isoString) return null;
  const s = String(isoString);
  return new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
};

export const formatTime = (isoString) => {
  const date = parseUtcDate(isoString);
  if (!date) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

export const formatCountdown = (seconds) => {
  if (seconds <= 0) return 'Refreshing...';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

export const getPnlColor = (value) => {
  const num = parseFloat(value);
  if (num > 0) return 'text-green-400';
  if (num < 0) return 'text-red-400';
  return 'text-gray-400';
};

export const formatFloatLen = (quantity) => {
  const qty = parseFloat(quantity);
  if (isNaN(qty)) return '';
  const intPart = Math.floor(Math.abs(qty)).toString();
  let decimals;
  if (intPart.length >= 5)      decimals = 0;
  else if (intPart.length >= 4) decimals = 1;
  else if (intPart.length >= 3) decimals = 2;
  else if (intPart.length >= 2) decimals = 3;
  else                          decimals = 4;
  return parseFloat(qty.toFixed(decimals)).toString();
};

export const formatCurrencyWithDecimals = (value, decimals) => {
  const num = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

// ---- string helpers ----

export const truncate = (str, maxLen, opts = {}) => {
  const {
    position = 'end',
    ellipsis = '…',
    keepWholeWords = false
  } = opts;

  if (typeof str !== 'string') str = String(str ?? '');
  if (!Number.isFinite(maxLen) || maxLen <= 0) return '';
  if (str.length === 0) return '';

  const seg = (typeof Intl !== 'undefined' && Intl.Segmenter)
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : null;

  const toGraphemes = (s) =>
    seg ? [...seg.segment(s)].map(x => x.segment) : Array.from(s);

  const g = toGraphemes(str);
  const e = toGraphemes(ellipsis);

  if (g.length <= maxLen) return str;
  if (maxLen <= e.length) return e.slice(0, maxLen).join('');

  const cap = maxLen - e.length;

  if (position === 'start') {
    // Keep the last `cap` characters
    return e.join('') + g.slice(g.length - cap).join('');
  }

  if (position === 'middle') {
    const left = Math.ceil(cap / 2);
    const right = Math.floor(cap / 2);
    return g.slice(0, left).join('') + e.join('') + g.slice(g.length - right).join('');
  }

  // position === 'end'
  let head = g.slice(0, cap).join('');

  if (keepWholeWords) {
    const i = head.lastIndexOf(' ');
    if (i > 0) head = head.slice(0, i);
  }

  return head + e.join('');
}

